import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useLocation } from 'react-router-dom';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, Stack, Drawer, Hidden } from '@mui/material';
// hooks
import useResponsive from '../../hooks/useResponsive';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// utils
import cssStyles from '../../utils/cssStyles';
// config
import { NAVBAR } from '../../config';
// components
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import { NavSectionVertical } from '../../components/nav-section';
//
import {
  sidebarConfigSchoolAdmin,
  sidebarConfigFpCo,
  sidebarTravelCo,
  callToActionTools,
  getSidebarConfigRootAdmin
} from './SidebarConfig';
import NavbarDocs from './NavbarDocs';
import NavbarAccount from './NavbarAccount';
import CollapseButton from './CollapseButton';

import { ProfileContext } from "../../contexts/ProfileContext";
import { BrandContext } from "../../contexts/BrandContext";
import { SelectorContext } from "../../contexts/SelectorContext";
import { CallToActionContext } from '../../contexts/CallToActionContext';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('xl')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
    const theme = useTheme();
    const brandContext = useContext(BrandContext);
    const { pathname } = useLocation();

    const isDesktop = useResponsive('up', 'xl');
    const profileContext = useContext(ProfileContext);
    const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } = useCollapseDrawer();
    const selectorContext = useContext(SelectorContext);
    
      useEffect(() => {
        if (isOpenSidebar) {
          onCloseSidebar();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [pathname]);
      
    let menuListItems = [];
    if(!brandContext.isLoading){
        switch(brandContext.brand.mode){
            case 'rootAdmin':{
                menuListItems = getSidebarConfigRootAdmin(profileContext,brandContext);
                break;
            }
            case 'schoolAdmin':{
                menuListItems = sidebarConfigSchoolAdmin(profileContext,brandContext);
                break;
            }
            case 'travelCo':{
                menuListItems = sidebarTravelCo(profileContext,brandContext,selectorContext);
                break;
            }
            case 'fpCo':{
                menuListItems = sidebarConfigFpCo(profileContext,brandContext,selectorContext);
                break;
            }
            default:
               // console.log('The mode has not been identified for suitable navigation');
        }
    }
    const {
      
        brand:{
          mode=''
        }
      } = brandContext;
      const callToAction = useContext(CallToActionContext);
      const tools = callToActionTools(brandContext,callToAction);

      

  const renderContent = (
      <Scrollbar
          sx={{
            height: 1,
            '& .simplebar-content': {
                height: 1,
                display: 'flex',
                flexDirection: 'column'
            },
          }}
      >
        <Stack
            spacing={3}
            sx={{
              pt: 3,
              pb: 2,
              px: 2.5,
              flexShrink: 0,
              ...(isCollapse && { alignItems: 'center' }),
            }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Logo sx={{cursor:'pointer'}} onClick={()=>{window.location.href='/'}} />

            {isDesktop && !isCollapse && (
                <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
            )}
          </Stack>

          <NavbarAccount isCollapse={isCollapse} />
        </Stack>

        {mode === 'schoolAdmin' &&
            <Hidden lgUp>
                <NavSectionVertical navConfig={[{items:tools}]} isCollapse={isCollapse} />
            </Hidden> 
          }


        <NavSectionVertical navConfig={menuListItems} isCollapse={isCollapse} />

        <Box sx={{ flexGrow: 1 }} />

        {!isCollapse && <NavbarDocs />}
      </Scrollbar>
  );
 // console.log(isCollapse);

  return (
      <RootStyle
          sx={{
            width: {
              xl: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
            },
            ...(collapseClick && {
              position: 'absolute',
            }),
          }}
      >
        {!isDesktop && (
            <Drawer open={isOpenSidebar} onClose={onCloseSidebar} PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}>
              {renderContent}
            </Drawer>
        )}

        {isDesktop && (
            <Drawer
                open
                variant="persistent"
                onMouseEnter={onHoverEnter}
                onMouseLeave={onHoverLeave}
                PaperProps={{
                  sx: {
                    width: NAVBAR.DASHBOARD_WIDTH,
                    borderRightStyle: 'dashed',
                    bgcolor: 'background.default',
                    transition: (theme) =>
                        theme.transitions.create('width', {
                          duration: theme.transitions.duration.standard,
                        }),
                    ...(isCollapse && {
                      width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
                    }),
                    ...(collapseHover && {
                      ...cssStyles(theme).bgBlur(),
                      boxShadow: (theme) => theme.customShadows.z24,
                    }),
                  },
                }}
            >
              {renderContent}
            </Drawer>
        )}
      </RootStyle>
  );
}
